import React, { useEffect, useState } from "react";
import { useDesktopNav } from "../navigation/DesktopNavContext";
import { DesktopBackLink } from "../navigation/ScreenBackButton";
import "../desktop/recDesktop.css";
import { materialTypeLabel, isServiceType, placeholderTone } from "./materialRecordModel";
import "./materialRecord.css";

/**
 * CrewPilot record-screen building blocks (first used by Materials).
 * Presentation only: no data access. See docs/engineering/crewpilot-ui-system.md
 * ("Record screens") for the pattern these implement.
 */

const ICON_PROPS = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
};

const ICON_PATHS = {
  back: <path d="m15 5-7 7 7 7" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  pencil: <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3ZM14 7l3 3" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  cube: (
    <>
      <path d="m12 3 8 4.2v9.6L12 21l-8-4.2V7.2L12 3Z" />
      <path d="m4.2 7.4 7.8 4.3 7.8-4.3M12 11.7V21" />
    </>
  ),
  tag: (
    <>
      <path d="M3.5 12.4V4.5a1 1 0 0 1 1-1h7.9a1 1 0 0 1 .7.3l7.1 7.1a1 1 0 0 1 0 1.4l-7.9 7.9a1 1 0 0 1-1.4 0l-7.1-7.1a1 1 0 0 1-.3-.7Z" />
      <circle cx="8" cy="8" r="1.3" />
    </>
  ),
  box: (
    <>
      <path d="M4 8h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8Z" />
      <path d="M3 4h18v4H3zM10 12h4" />
    </>
  ),
  barcode: <path d="M4 5v14M7 5v14M11 5v14M14 5v14M18 5v14M20.5 5v14" />,
  doc: (
    <>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  upload: (
    <>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
    </>
  ),
  select: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8.5 12.3 2.4 2.4 4.6-5" />
    </>
  ),
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 12a1 1 0 0 0 1 .9h8a1 1 0 0 0 1-.9l1-12M9 7V4h6v3" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7H8l1.2-2h5.6L16 7h2.5A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9Z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  image: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="m4.5 17.5 4.5-4.5 3.5 3.5 2.5-2.5 4.5 4" />
    </>
  ),
  list: (
    <>
      <path d="M9 7h11M9 12h11M9 17h11" />
      <circle cx="4.5" cy="7" r="1.1" fill="currentColor" />
      <circle cx="4.5" cy="12" r="1.1" fill="currentColor" />
      <circle cx="4.5" cy="17" r="1.1" fill="currentColor" />
    </>
  ),
  grid: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </>
  ),
  cloud: (
    <>
      <path d="M7 18a4 4 0 0 1-.6-7.95A5.5 5.5 0 0 1 17 8.6 4.7 4.7 0 0 1 17 18H7Z" />
      <path d="m9.5 13.5 2 2 3.5-3.7" />
    </>
  ),
  building: (
    <>
      <path d="M5 20V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15M15 10h3a1 1 0 0 1 1 1v9M3.5 20h17" />
      <path d="M8.5 8h3M8.5 12h3M8.5 16h3" />
    </>
  ),
  star: <path d="m12 3.6 2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 17l-5.2 2.8 1-5.9L3.5 9.8l5.9-.8L12 3.6Z" />,
  pin: (
    <>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" />
      <circle cx="12" cy="10" r="2.3" />
    </>
  ),
  chart: <path d="M4 19V5M4 19h16M8 15v-4M12 15V8M16 15v-6" />,
  cart: (
    <>
      <path d="M3.5 4.5h2.3l2 10.2h9.4l1.8-7.4H7" />
      <circle cx="9.5" cy="18.8" r="1.3" />
      <circle cx="16.8" cy="18.8" r="1.3" />
    </>
  ),
  invoice: (
    <>
      <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7l-4-4Z" />
      <path d="M14 3v4h4M9 12h6M9 16h4" />
    </>
  ),
  task: (
    <>
      <rect x="5" y="4.5" width="14" height="16" rx="2.2" />
      <path d="M9 4.5h6v2.6H9zM8.8 13.2l2.2 2.2 4.2-4.4" />
    </>
  ),
  flag: <path d="M6 20V4.5M6 5h11l-2.2 3.5L17 12H6" />,
  home: <path d="M4 11.2 12 4l8 7.2V20a1 1 0 0 1-1 1h-4.5v-5.5h-5V21H5a1 1 0 0 1-1-1v-8.8Z" />,
  chat: <path d="M5 5.5h14a1 1 0 0 1 1 1V16a1 1 0 0 1-1 1h-7l-4.5 3.5V17H5a1 1 0 0 1-1-1V6.5a1 1 0 0 1 1-1Z" />,
  share: (
    <>
      <path d="M12 15V4M8 8l4-4 4 4" />
      <path d="M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6a1.5 1.5 0 0 0-1.5-1.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
    </>
  ),
  link: <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  folder: <path d="M3.5 7a1.5 1.5 0 0 1 1.5-1.5h4l2 2.5h8A1.5 1.5 0 0 1 20.5 9.5V18a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18V7Z" />,
  dollar: <path d="M12 4v16M16 8.2c-.6-1.2-2-2-4-2-2.3 0-4 1.1-4 2.9 0 4 8.2 2 8.2 6 0 1.8-1.8 3-4.2 3-2.1 0-3.7-.9-4.3-2.3" />,
  chevron: <path d="m9 5 7 7-7 7" />,
  filter: <path d="M4 5h16l-6 7.5V19l-4-2v-4.5L4 5Z" />,
  sort: <path d="M4 7h16M7 12h10M10 17h4" />,
  wrench: (
    <path d="M14.7 6.3a4 4 0 0 0-5 5L3.5 17.5a1.8 1.8 0 0 0 2.6 2.6l6.2-6.2a4 4 0 0 0 5-5l-2.5 2.5-2.1-.5-.5-2.1 2.5-2.5Z" />
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M9 21h6" />
    </>
  ),
};

export function RecIcon({ name, className = "" }) {
  return (
    <svg className={`rec-icon ${className}`.trim()} {...ICON_PROPS}>
      {ICON_PATHS[name]}
    </svg>
  );
}

/**
 * Visual identity tile. Renders `imageUrl` when a material has one, else a
 * stable placeholder. Callers pass the same props today and later, so the
 * layout never changes when Material images arrive.
 */
export function RecThumb({ name, type, imageUrl, size = "lg" }) {
  const service = isServiceType(type);
  const [failedUrl, setFailedUrl] = useState("");
  // An expired/missing image must never leave a broken tile: fall back.
  const showImage = Boolean(imageUrl) && failedUrl !== imageUrl;

  return (
    <span
      className={`rec-thumb rec-thumb--${size} rec-thumb--${placeholderTone(name)}${showImage ? " has-image" : ""}`}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          className="rec-thumb__img"
          loading="lazy"
          decoding="async"
          onError={() => setFailedUrl(imageUrl)}
        />
      ) : (
        <RecIcon name={service ? "wrench" : "cube"} />
      )}
    </span>
  );
}

export function RecTypePill({ type }) {
  return (
    <span className={`rec-pill rec-pill--type${isServiceType(type) ? " rec-pill--service" : ""}`}>
      {materialTypeLabel(type)}
    </span>
  );
}

export function RecStatusPill({ active }) {
  return (
    <span className={`rec-pill ${active ? "rec-pill--ok" : "rec-pill--off"}`}>
      {active ? <RecIcon name="check" /> : null}
      {active ? "Active" : "Inactive"}
    </span>
  );
}

/** Compact page bar: leading control, centered title, trailing spacer/action. */
export function RecBar({ title, onBack, leading = "back", trailing = null, backLabel = "" }) {
  const desktopNav = useDesktopNav();
  if (desktopNav) {
    // Desktop: contextual "‹ Parent" return, left-aligned display title, actions on the right.
    return (
      <header className="rec-bar rec-bar--desk">
        <DesktopBackLink onClick={onBack} label={backLabel || desktopNav.parentLabel || "Back"} />
        <div className="rec-bar__row">
          <h1 className="rec-bar__title">{title}</h1>
          <div className="rec-bar__side">{trailing}</div>
        </div>
      </header>
    );
  }
  return (
    <header className="rec-bar">
      <button
        type="button"
        className="rec-bar__btn"
        onClick={onBack}
        aria-label={
          backLabel
            ? `Back to ${backLabel}`
            : leading === "close"
              ? "Close"
              : "Back"
        }
      >
        <RecIcon name={leading === "close" ? "close" : "back"} />
      </button>
      <h1 className="rec-bar__title">{title}</h1>
      <div className="rec-bar__side">{trailing}</div>
    </header>
  );
}

export function RecStat({ icon, label, value, wide = false, hero = false }) {
  return (
    <div className={`rec-stat${wide ? " rec-stat--wide" : ""}${hero ? " rec-stat--hero" : ""}`}>
      <span className="rec-stat__icon">
        <RecIcon name={icon} />
      </span>
      <span className="rec-stat__body">
        <span className="rec-stat__label">{label}</span>
        <span className="rec-stat__value">{value}</span>
      </span>
    </div>
  );
}

/** Label + control. Controls are 44px high, 16px text (no iOS zoom). */
export function RecField({ label, htmlFor, required = false, icon, error, hint, children }) {
  return (
    <div className="rec-field">
      <label className="rec-field__label" htmlFor={htmlFor}>
        {icon ? <RecIcon name={icon} /> : null}
        {label}
        {required ? <span className="rec-req" aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {error ? (
        <div className="rec-field__error" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="rec-field__hint">{hint}</div>
      ) : null}
    </div>
  );
}

/**
 * True while the on-screen keyboard is open.
 *
 * iOS and modern Android overlay the keyboard on the layout viewport (only the
 * *visual* viewport shrinks), others resize the layout viewport itself. We
 * compare the visual viewport with the tallest viewport seen, so both cases
 * are detected. While open we:
 *   - mark <html class="rec-kb-open"> so the bottom bar and Ashley step aside
 *   - publish --rec-kb-inset so the sticky Cancel/Save lifts to sit directly
 *     above the keyboard instead of behind it.
 */
export function useKeyboardOpen(enabled = true) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !window.visualViewport) {
      return undefined;
    }
    const viewport = window.visualViewport;
    const root = document.documentElement;
    let tallest = Math.max(window.innerHeight, viewport.height);

    const update = () => {
      tallest = Math.max(tallest, window.innerHeight, viewport.height);
      const covered = tallest - viewport.height - viewport.offsetTop;
      const isOpen = tallest - viewport.height > 150;
      // Only the part of the layout viewport the keyboard actually covers.
      const inset = isOpen ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0;
      root.style.setProperty("--rec-kb-inset", `${Math.round(inset)}px`);
      setOpen(isOpen && covered > -1);
    };

    const onOrientation = () => {
      tallest = 0;
      update();
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    window.addEventListener("orientationchange", onOrientation);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("orientationchange", onOrientation);
      root.style.removeProperty("--rec-kb-inset");
    };
  }, [enabled]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("rec-kb-open", open);
    return () => root.classList.remove("rec-kb-open");
  }, [open]);

  return open;
}

/**
 * While the keyboard is open, keep the focused field in the part of the screen
 * that is still visible: above the keyboard and above the sticky Cancel/Save.
 * Browsers scroll fields into view, but not around our sticky bar.
 */
export function useKeepFocusedFieldVisible(enabled = true) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return undefined;

    function onFocusIn(event) {
      const el = event.target;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;

      window.setTimeout(() => {
        const vv = window.visualViewport;
        if (!vv || document.activeElement !== el) return;
        if (!document.documentElement.classList.contains("rec-kb-open")) return;

        const barHeight = document.querySelector(".rec-actions")?.offsetHeight || 64;
        const top = vv.offsetTop + 8;
        const bottom = vv.offsetTop + vv.height - barHeight - 8;
        const rect = el.getBoundingClientRect();

        if (rect.bottom > bottom || rect.top < top) {
          const centered = top + (bottom - top - rect.height) / 2;
          window.scrollBy({ top: rect.top - centered, behavior: "smooth" });
        }
      }, 350);
    }

    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [enabled]);
}
